/* @flow */

import React, { useCallback } from 'react';

type AccessibleButtonPropType = {
  +'aria-controls'?: string,
  +'aria-expanded'?: boolean,
  +'aria-haspopup'?: boolean,
  +'aria-label'?: string,
  +'aria-labelledby'?: string,
  +children: React.Node,
  +className?: string,
  +id?: string,
  +onClick?: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement> | SyntheticKeyboardEvent<HTMLElement>) => void,
};

const AccessibleButton = (accessibleButtonProps: AccessibleButtonPropType, ref: React.RefSetter<HTMLElement>): React.Node => {
  const {
    'aria-controls': ariaControls,
    'aria-expanded': ariaExpanded,
    'aria-haspopup': ariaHasPopup,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    children,
    className,
    id,
    onClick,
  } = accessibleButtonProps;

  const handleKeyDown = useCallback(
    (event: SyntheticKeyboardEvent<HTMLElement>) => {
      const { key } = event;

      if (typeof onClick === 'function' && (key === 'Enter' || key === ' ')) {
        event.preventDefault();
        event.stopPropagation();

        onClick(event);
      }
    },
    [onClick],
  );

  if (onClick === undefined) {
    // Disabled button
    return (
      <div aria-hidden className={className} ref={ref}>
        {children}
      </div>
    );
  }

  // Enabled button
  return (
    <div
      aria-controls={ariaControls}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHasPopup}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      className={className}
      id={id}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      ref={ref}
      role='button'
      tabIndex={0}
    >
      {children}
    </div>
  );
};

export default React.forwardRef<AccessibleButtonPropType, HTMLElement>(AccessibleButton) as React.ComponentType<{
  ...AccessibleButtonPropType,
  +ref?: React.RefSetter<HTMLElement> | { current: HTMLElement | null, ... } | null,
}>;
